import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { templateService } from '../services/api';
import { toast } from 'react-hot-toast';
import MetaTemplateList from '../components/MetaTemplateList';
import MetaTemplateCreateDialog from '../components/MetaTemplateCreateDialog';
import MetaTemplateBroadcastDialog from '../components/MetaTemplateBroadcastDialog';
import MetaTemplatePreviewDialog from '../components/MetaTemplatePreviewDialog';
import {
  PlusIcon,
  RefreshIcon
} from '../assets/icons';

const MetaWhatsAppTemplates = () => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const refreshTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await templateService.refreshMetaTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (err) {
      console.error('Error refreshing templates:', err);
      setError('Failed to refresh templates. Please try again.');
      toast.error('Failed to refresh templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await templateService.listMetaTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      } else {
        setError(response.message || 'Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    fetchTemplates();
    toast.success('Template created successfully! It is now pending approval from Meta.');
  };

  const handleBroadcast = (template) => {
    setSelectedTemplate(template);
    setShowBroadcastDialog(true);
  };

  const handleBroadcastClose = () => {
    setShowBroadcastDialog(false);
    setSelectedTemplate(null);
  };

  const handleTemplateClick = (template) => {
    setPreviewTemplateId(template.id);
    setShowPreviewDialog(true);
  };

  const handlePreviewClose = () => {
    setShowPreviewDialog(false);
    setPreviewTemplateId(null);
  };

  const filteredTemplates = templates.filter(template => {
    const matchesStatus = statusFilter === 'all' || template.status?.toLowerCase() === statusFilter.toLowerCase();
    const matchesSearch = !searchTerm || template.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('metaTemplates.title', 'Meta WhatsApp Templates')}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('metaTemplates.description', 'Create and manage WhatsApp message templates for marketing and utility messages')}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={refreshTemplates}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshIcon className="w-4 h-4 mr-2" />
              {t('common.refresh', 'Refresh')}
            </button>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              {t('metaTemplates.createTemplate', 'Create Template')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={t('common.search', 'Search templates...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">{t('metaTemplates.allStatuses', 'All Statuses')}</option>
            <option value="approved">{t('metaTemplates.approved', 'Approved')}</option>
            <option value="pending">{t('metaTemplates.pending', 'Pending')}</option>
            <option value="rejected">{t('metaTemplates.rejected', 'Rejected')}</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <MetaTemplateList
        templates={filteredTemplates}
        loading={loading}
        onBroadcast={handleBroadcast}
        onRefresh={fetchTemplates}
        onTemplateClick={handleTemplateClick}
      />

      {showCreateDialog && (
        <MetaTemplateCreateDialog
          isOpen={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {showBroadcastDialog && selectedTemplate && (
        <MetaTemplateBroadcastDialog
          isOpen={showBroadcastDialog}
          onClose={handleBroadcastClose}
          template={selectedTemplate}
        />
      )}

      {showPreviewDialog && previewTemplateId && (
        <MetaTemplatePreviewDialog
          isOpen={showPreviewDialog}
          onClose={handlePreviewClose}
          templateId={previewTemplateId}
        />
      )}
    </div>
  );
};

export default MetaWhatsAppTemplates;

